import {Box, Button, ListItemText, MenuItem, Stack, SvgIcon, TextField, Typography} from "@mui/material";
import Property from "crqlar-react-common/dist/components/Property";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {firstFontName, FontSource, FontValue, googleFontFamilies, useFont} from "./fonts";
import FontFileUpload from "./parts/FontFileUpload";
import GoogleFontDialog from "./parts/GoogleFontDialog";

const GoogleFontsIcon = () => (
    <SvgIcon viewBox={'0 -960 960 960'}>
        <path d={'M120-600v-160q0-33 23.5-56.5T200-840h280v80H200v160h-80Zm300 300-56-58 82-82H120v-80h326l-82-82 56-58 180 180-180 180ZM200-120q-33 0-56.5-23.5T120-200v-160h80v160h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm540-440q-6 0-8-6-16-61-60.5-105.5T566-732q-6-2-6-8 0-7 6-8 61-16 105.5-60.5T732-914q2-6 8-6t8 6q16 61 60.5 105.5T914-748q6 1 6 8 0 6-6 8-61 16-105.5 60.5T748-566q-1 6-8 6Z'}/>
    </SvgIcon>
);

// The menu shows each source with a line under it; the field only its name
const SOURCES: {value: FontSource, label: string, description: string}[] = [
    {value: 'STANDARD', label: 'Standard', description: 'The page\'s own fonts'},
    {value: 'GOOGLE', label: 'Google Fonts (Recommended)', description: 'Managed by Google'},
    {value: 'OWN', label: 'Own font', description: 'Upload your font file'},
];

interface Props {
    value: FontValue
    onChange: (value: FontValue) => void
    errors?: {fontUrl?: string}
    disabled?: boolean
}

/**
 * A page's font as Property rows: the source, then a Google font from the dialog or an uploaded own font.
 * Loads the chosen font into this page, so the host's preview shows it.
 */
const FontPickerFields = ({value, onChange, errors, disabled}: Props) => {
    const {t} = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const {fontSource, fontUrl, fontFamily} = value;
    const fontError = errors?.fontUrl && t(errors.fontUrl);
    // Chosen only with its link and its name, like the own font's row
    const googleFont = fontSource === 'GOOGLE' && fontUrl ? firstFontName(fontFamily) : undefined;

    useFont(fontSource, errors?.fontUrl ? undefined : fontUrl, fontFamily);

    // A new source starts without a font; blank, not null, so saving clears the old one
    const pickSource = (next: FontSource) => {
        if (next !== fontSource) onChange({fontSource: next, fontUrl: '', fontFamily: ''});
    };

    return (
        <>
            <Property title={t('Source')}>
                <TextField
                    select
                    size={'small'}
                    fullWidth
                    value={fontSource}
                    onChange={event => pickSource(event.target.value as FontSource)}
                    disabled={disabled}
                    inputProps={{'aria-label': t('Source')}}
                    SelectProps={{renderValue: source => t(SOURCES.find(option => option.value === source)?.label ?? '')}}
                >
                    {SOURCES.map(source => (
                        <MenuItem key={source.value} value={source.value}>
                            <ListItemText primary={t(source.label)} secondary={t(source.description)}/>
                        </MenuItem>
                    ))}
                </TextField>
            </Property>

            {fontSource === 'GOOGLE' && (
                <Property title={t('Font')}>
                    <Stack gap={0.5} width={'100%'}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                minHeight: 48,
                                pl: 2,
                                pr: 0.75,
                                border: '1px solid',
                                borderColor: fontError ? 'error.main' : 'divider',
                                borderRadius: 1,
                            }}
                        >
                            {googleFont ? (
                                <Typography noWrap sx={{fontFamily: `"${googleFont}", serif`, fontSize: 18}}>{googleFont}</Typography>
                            ) : (
                                <Typography variant={'body1'} color={'text.secondary'}>{t('No font chosen')}</Typography>
                            )}
                            <Button
                                variant={'outlined'}
                                size={'small'}
                                startIcon={<GoogleFontsIcon/>}
                                disabled={disabled}
                                onClick={() => setDialogOpen(true)}
                                sx={{flexShrink: 0}}
                            >
                                {googleFont ? t('Change font') : t('Choose font')}
                            </Button>
                        </Box>
                        {!!fontError && <Typography variant={'body2'} color={'error'}>{fontError}</Typography>}
                    </Stack>
                </Property>
            )}

            {fontSource === 'OWN' && (
                <Property title={t('Font file')}>
                    <FontFileUpload
                        fontUrl={fontUrl}
                        fontFamily={fontFamily}
                        error={fontError || undefined}
                        disabled={disabled}
                        onUploaded={font => onChange({fontSource: 'OWN', ...font})}
                        onRemove={() => onChange({fontSource: 'OWN', fontUrl: '', fontFamily: ''})}
                    />
                </Property>
            )}

            <GoogleFontDialog
                open={dialogOpen}
                fontUrl={googleFont ? fontUrl : undefined}
                onClose={() => setDialogOpen(false)}
                onUse={link => {
                    onChange({fontSource: 'GOOGLE', fontUrl: link, fontFamily: googleFontFamilies(link)[0]});
                    setDialogOpen(false);
                }}
            />
        </>
    );
};

export default FontPickerFields;
